import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useUser } from '../contexts/UserContext';
import { isSplashDone, onSplashDone } from '../lib/splash';

const STALE_MS = 30_000; // navigation checks within this window are no-ops

// Badges the member has earned but not yet been congratulated for (047).
//
// Most badges are earned while the member is somewhere else — a moderator
// approves their verification, a vote lands on an old post — so the card
// can't fire at the moment of earning. It fires at the next natural check:
//   • app open, once the cold-start LoadingScreen has gone (it sits on top at
//     zIndex 9999, so an animation started under it plays unseen)
//   • `verified` flipping in UserContext (invite code, refreshProfile)
//   • navigation, throttled — posting navigates, which is how First Post shows
//   • resuming from the background (a Capacitor WebView is resumed, not reloaded)
//
// ⚠️ Never over another modal. If one is open (useModalDismiss's
// data-modal-open counter), the result is dropped WITHOUT marking anything
// seen, and the next check picks it up.
export function useBadgeCelebration() {
  const { session, userData } = useUser();
  const userId = session?.user?.id;
  const verified = userData?.verified === true;
  const { pathname } = useLocation();

  const [badges, setBadges] = useState(null);
  const lastCheckRef = useRef(0);
  const showingRef = useRef(false);
  const [splashDone, setSplashDone] = useState(isSplashDone);

  useEffect(() => {
    if (splashDone) return undefined;
    return onSplashDone(() => setSplashDone(true));
  }, [splashDone]);

  const check = useCallback(async (force = false) => {
    if (!userId || !splashDone || showingRef.current) return;
    const now = Date.now();
    if (!force && now - lastCheckRef.current < STALE_MS) return;
    lastCheckRef.current = now;

    const { data, error } = await supabase.rpc('get_unseen_badges');
    if (error) {
      console.error('get_unseen_badges:', error);
      return;
    }
    if (!data?.length || showingRef.current) return;
    if (document.body.dataset.modalOpen) {
      lastCheckRef.current = 0; // retry at the next trigger
      return;
    }
    // Verified DSP leads: it has the seal animation, and it's the big one.
    const ordered = [...data].sort(
      (a, b) => (b.id === 'verified_dsp') - (a.id === 'verified_dsp')
    );
    showingRef.current = true;
    setBadges(ordered);
  }, [userId, splashDone]);

  // App open, account switch, splash end, verification flip.
  useEffect(() => {
    check(true);
  }, [check, verified]);

  // Navigation (throttled by STALE_MS).
  useEffect(() => {
    check();
  }, [pathname, check]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [check]);

  const dismiss = useCallback(async () => {
    const ids = (badges ?? []).map((b) => b.id);
    setBadges(null);
    showingRef.current = false;
    lastCheckRef.current = Date.now();
    if (!ids.length) return;
    // If this fails the card simply shows again next time — better than
    // losing it — but say so rather than failing silently (pipeline review #4).
    const { error } = await supabase.rpc('mark_badges_seen', { p_badge_ids: ids });
    if (error) console.error('mark_badges_seen:', error);
  }, [badges]);

  return { badges, dismiss };
}

export default useBadgeCelebration;
